<template>
  <ModalComponent
    :submitButtonText="$t('forwards.deals.move.submit')"
    :hideSubmitButton="true"
    size="lg"
    @close="close"
    @setForceToggle="setForceToggle"
  >
    <template #title>{{ $t("forwards.deals.move.title") }}</template>

    <template #body>
      <div v-if="slice" class="p-1">
        <p class="text-body-tertiary">
          {{
            $t("forwards.deals.move.body", {
              period: slice.period_label,
              mwh: formatNumberWithSpaces(slice.volume_mwh),
            })
          }}
        </p>

        <label class="form-label" :for="targetId">{{
          $t("forwards.deals.move.target")
        }}</label>
        <select :id="targetId" v-model="targetDeliveryId" class="form-select">
          <option :value="null" disabled>
            {{ $t("forwards.deals.move.target_placeholder") }}
          </option>
          <option
            v-for="option in targetOptions"
            :key="option.id"
            :value="option.id"
          >
            {{ option.label }}
          </option>
        </select>

        <label class="form-label mt-3" :for="volumeId">{{
          $t("forwards.deals.move.volume")
        }}</label>
        <MwhInputComponent
          :id="volumeId"
          v-model="volume"
          :min="1"
          :max="slice.volume_mwh"
          :placeholder="$t('forwards.deals.move.volume_placeholder')"
        />
        <div class="form-text">{{ $t("forwards.deals.move.volume_hint") }}</div>
        <div v-if="volumeOutOfRange" class="error-note fs-10 mt-1" role="alert">
          <span>{{
            $t("forwards.deals.move.volume_out_of_range", {
              mwh: formatNumberWithSpaces(slice.volume_mwh),
            })
          }}</span>
        </div>

        <p v-if="errorMessage" class="error-note mt-2 mb-0" role="alert">
          <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
          <span>{{ errorMessage }}</span>
        </p>
      </div>
    </template>

    <template #footer>
      <button type="button" class="btn btn-link text-secondary" @click="close">
        {{ $t("forwards.deals.common.cancel") }}
      </button>
      <ActionButtonWithSpinner
        :busy="busy"
        :disabled="targetDeliveryId === null || volumeOutOfRange || busy"
        variant="btn-primary"
        @click="submit"
      >
        {{ $t("forwards.deals.move.submit") }}
      </ActionButtonWithSpinner>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import ActionButtonWithSpinner from "@/components/common/ActionButtonWithSpinner.vue";
import ModalComponent from "@/components/common/ModalComponent.vue";
import { formatNumberWithSpaces } from "@/composables/formatUtils";
import MwhInputComponent from "@/forwards2/components/MwhInputComponent.vue";
import { isDeliveryFrozen } from "@/forwards2/deals/deliveryFrozen";
import { useDeliveryWindowText } from "@/forwards2/deals/useDeliveryWindowText";
import type {
  DeliveryResponse,
  SliceResponse,
} from "@/forwards2/forwardsTypes";
import { computed, ref, useId, watch } from "vue";

/**
 * Move a slice to another delivery of the same deal, or split part of it there.
 *
 * An empty volume moves the whole slice; a partial volume leaves the remainder where it is. The
 * escrow already attributed to the slice follows the volume, which is why the target is limited to
 * deliveries of this deal.
 */
const props = withDefaults(
  defineProps<{
    slice: SliceResponse | null;
    deliveries: DeliveryResponse[];
    /** The delivery the slice sits in now — never a target for itself. */
    sourceDeliveryId: number | null;
    busy?: boolean;
    errorMessage?: string | null;
  }>(),
  { busy: false, errorMessage: null }
);

const emit = defineEmits<{
  submit: [targetDeliveryId: number, volumeMwh: number | null];
}>();

const model = defineModel<boolean>({ required: true });

const deliveryWindowText = useDeliveryWindowText();

const targetId = useId();
const volumeId = useId();
const forceToggle = ref<(value: boolean) => void>();

/**
 * `ModalComponent` hands its toggle over during its own setup, before any watcher has run, so the
 * current model value is applied at once — otherwise a modal mounted already open stays hidden.
 */
const setForceToggle = (toggle: (value: boolean) => void) => {
  forceToggle.value = toggle;
  toggle(model.value);
};
const targetDeliveryId = ref<number | null>(null);
const volume = ref<number | null>(null);

const targetOptions = computed(() =>
  props.deliveries
    .filter(
      (delivery) =>
        delivery.id !== props.sourceDeliveryId &&
        !isDeliveryFrozen(delivery.transaction_status)
    )
    .map((delivery) => ({
      id: delivery.id,
      label: deliveryWindowText(delivery),
    }))
);

watch(model, (isOpen) => {
  forceToggle.value?.(isOpen);
  if (isOpen) {
    targetDeliveryId.value = null;
    volume.value = null;
  }
});

const close = () => {
  model.value = false;
};

const volumeOutOfRange = computed(
  () =>
    volume.value !== null &&
    props.slice !== null &&
    (volume.value < 1 || volume.value > props.slice.volume_mwh)
);

const submit = () => {
  if (targetDeliveryId.value !== null && !volumeOutOfRange.value)
    emit("submit", targetDeliveryId.value, volume.value);
};
</script>
